import React, {useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {IoIosClose} from 'react-icons/io'
import {useSupersedingCycles, useClaimFiles} from '../../../../hooks/useSuperseding'
import {useAnswerMedicalEnquiry} from '../../../../hooks/useMedicalEnquiry'
import {downloadMedicalEnquiryDocument} from '../../../../services/medicalEnquiryService'
import {getClaimFileUrl} from '../../../../services/supersedingService'
import {SupersedingCycle, ClaimFile} from '../../../../types/superseding'
import {toast} from 'react-toastify'
import {
  Download,
  Eye,
  File,
  Image,
  FileText,
  Send,
  GitPullRequest,
} from 'lucide-react'
import moment from 'moment'

interface Props {
  show: boolean
  onHide: () => void
  claimNumber: string
  username: string
  isImpersonation?: boolean
}

const SupersedingTrackingModal: React.FC<Props> = ({show, onHide, claimNumber, username, isImpersonation}) => {
  const {formatMessage: f} = useIntl()
  const {supersedingCycles, isLoading} = useSupersedingCycles(claimNumber)
  const {claimFiles} = useClaimFiles(claimNumber, show)
  const answerMeMutation = useAnswerMedicalEnquiry()

  const [replyText, setReplyText] = useState<Record<number, string>>({})
  const [downloadingKeys, setDownloadingKeys] = useState<Record<string, boolean>>({})
  const [viewModalOpen, setViewModalOpen] = useState(false)
  const [viewingDocument, setViewingDocument] = useState<{
    fileName: string
    base64Data: string
    contentType: string
  } | null>(null)

  const handleReplyChange = (idMe: number, text: string) => {
    setReplyText((prev) => ({...prev, [idMe]: text}))
  }

  const handleSubmitReply = async (idMe: number) => {
    const message = replyText[idMe]?.trim()
    if (!message) return

    try {
      await answerMeMutation.mutateAsync({
        claimNumber,
        idMe,
        message,
        username,
      })
      toast.success(f({id: 'CLAIM.INPATIENT.SUPERSEDING_REPLY_SUBMIT'}) + ' Success')
      setReplyText((prev) => ({...prev, [idMe]: ''}))
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to submit reply')
    }
  }

  const getFileIcon = (fileName: string) => {
    const extension = fileName?.split('.').pop()?.toLowerCase()
    switch (extension) {
      case 'pdf':
        return <FileText className='text-danger' size={16} />
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <Image className='text-primary' size={16} />
      default:
        return <File className='text-secondary' size={16} />
    }
  }

  const getDocName = (doc: {fileName?: string; title?: string}) =>
    doc.fileName || doc.title || 'Document'

  // Display name for a stored file: drop any folder path and the glNumber prefix.
  const cleanFileName = (name: string, glNumber: string) => {
    let display = name?.split('/').pop() || name || ''
    if (glNumber) {
      display = display.split(glNumber).join('').replace(/^[_\-\s]+/, '').replace(/[_\-\s]+$/, '')
    }
    return display || name
  }

  // Superseding documents are grouped under a label like
  // "IGL Superseding Document {glNumber}", so match the cycle by glNumber in the label.
  const getCycleFiles = (glNumber: string): ClaimFile[] =>
    claimFiles.filter((file) => !!glNumber && file.label?.includes(glNumber))

  const getCycleTypeLabel = (cycle: SupersedingCycle) => {
    const gl = (cycle.glNumber || '').toUpperCase()
    if (gl.startsWith('IGL')) return 'IGL Superseding'
    if (gl.startsWith('FGL')) return 'FGL Superseding'
    return `${cycle.glType || ''} Superseding`.trim()
  }

  // Open the file's signed URL in a new tab. The GCS signed URLs don't allow
  // cross-origin fetch, so an on-page blob download fails with a CORS error.
  const handleDownload = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  // Resolve a signed URL for a stored claim file, then open it in a new tab.
  const handleDownloadClaimFile = async (fileName: string) => {
    setDownloadingKeys((prev) => ({...prev, [fileName]: true}))
    try {
      const url = await getClaimFileUrl(claimNumber, fileName)
      if (!url) {
        toast.error('Failed to download document')
        return
      }
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (error) {
      toast.error('Failed to download document')
    } finally {
      setDownloadingKeys((prev) => ({...prev, [fileName]: false}))
    }
  }

  const handleDownloadBase64 = async (fileName: string) => {
    try {
      const response = await downloadMedicalEnquiryDocument(claimNumber, fileName)
      const byteCharacters = atob(response.base64Data)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], {type: response.contentType})

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = response.fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      toast.error('Failed to download document')
    }
  }

  const handleViewBase64 = async (fileName: string) => {
    try {
      const response = await downloadMedicalEnquiryDocument(claimNumber, fileName)
      setViewingDocument({
        fileName: response.fileName,
        base64Data: response.base64Data,
        contentType: response.contentType,
      })
      setViewModalOpen(true)
    } catch (error) {
      toast.error('Failed to view document')
    }
  }

  const getStatusBadge = (status: string | null) => {
    const normalized = (status || '').toLowerCase()
    const base = 'badge rounded-pill fw-semibold'
    if (normalized === 'approved') {
      return <span className={`${base} badge-light-success`}>{f({id: 'CLAIM.INPATIENT.SUPERSEDING_STATUS_APPROVED'}) || 'Approved'}</span>
    }
    if (normalized === 'rejected') {
      return <span className={`${base} badge-light-danger`}>{f({id: 'CLAIM.INPATIENT.SUPERSEDING_STATUS_REJECTED'}) || 'Rejected'}</span>
    }
    return <span className={`${base} badge-light-warning`}>{f({id: 'CLAIM.INPATIENT.SUPERSEDING_STATUS_REQUESTED'}) || 'Requested'}</span>
  }

  const sortedCycles = [...supersedingCycles].sort(
    (a, b) => new Date(b.supersedingDate).getTime() - new Date(a.supersedingDate).getTime()
  )

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        size='lg'
        dialogClassName='modal-dialog-centered'
        contentClassName='rounded'
      >
        <div className='modal-header py-8 px-8 border-bottom d-flex justify-content-between align-items-center'>
          <div className='d-flex align-items-center gap-3'>
            <GitPullRequest className='text-primary' size={24} />
            <h2 className='fw-bold mb-0'>
              {f({id: 'CLAIM.INPATIENT.SUPERSEDING_TRACKING_TITLE'}) || 'Superseding Request History'}
            </h2>
          </div>
          <div className='btn btn-sm btn-icon btn-active-color-primary' onClick={onHide}>
            <IoIosClose size={28} />
          </div>
        </div>

        <div className='modal-body scroll-y px-8 py-6' style={{maxHeight: '75vh'}}>
          {isLoading ? (
            <div className='d-flex justify-content-center p-5'>
              <span className='spinner-border text-primary' style={{width: '3rem', height: '3rem'}} />
            </div>
          ) : sortedCycles.length === 0 ? (
            <div className='text-center text-muted py-10'>No tracking data available</div>
          ) : (
            <div className='d-flex flex-column gap-6'>
              {sortedCycles.map((cycle, index) => {
                const cycleFiles = getCycleFiles(cycle.glNumber)
                return (
                  <div key={index} className='card border border-gray-300 rounded'>
                    <div className='card-header bg-light d-flex justify-content-between align-items-center min-h-50px px-5 rounded-top'>
                      <div className='d-flex align-items-center gap-2'>
                        <span className='fw-bold text-gray-800'>{getCycleTypeLabel(cycle)}</span>
                        <span className='text-gray-400'>·</span>
                        <span className='text-gray-500 fs-7' style={{fontFamily: 'monospace'}}>
                          {cycle.glNumber}
                        </span>
                      </div>
                      <div className='d-flex align-items-center gap-3'>
                        <span className='text-gray-500 fs-7'>
                          {moment(cycle.supersedingDate).format('DD-MM-YYYY HH:mm')}
                        </span>
                        {getStatusBadge(cycle.status)}
                      </div>
                    </div>

                    <div className='card-body px-5 py-4'>
                      <div className='d-flex mb-2'>
                        <div className='text-gray-500 flex-shrink-0' style={{width: '110px'}}>
                          {f({id: 'CLAIM.INPATIENT.SUPERSEDING_CATEGORY'}) || 'Category'}
                        </div>
                        <div className='fw-bold text-gray-800'>{cycle.supersedingCategory || '-'}</div>
                      </div>

                      <div className='d-flex mb-4'>
                        <div className='text-gray-500 flex-shrink-0' style={{width: '110px'}}>Message</div>
                        <div className='text-gray-800'>{cycle.supersedingMessage || '-'}</div>
                      </div>

                      {cycleFiles.length > 0 && (
                        <div className='mb-4'>
                          <div className='text-uppercase text-gray-500 fw-bold fs-8 mb-2' style={{letterSpacing: '0.5px'}}>
                            Superseding Documents
                          </div>
                          <div className='d-flex flex-column gap-2'>
                            {cycleFiles.map((file) => {
                              const displayName = cleanFileName(file.name, cycle.glNumber)
                              return (
                                <div
                                  key={file.id || file.name}
                                  className='d-flex align-items-center justify-content-between bg-light rounded px-4 py-3'
                                >
                                  <div className='d-flex align-items-center gap-3 text-truncate'>
                                    {getFileIcon(file.name)}
                                    <span className='text-gray-800 fs-7 text-truncate'>{displayName}</span>
                                  </div>
                                  <button
                                    className='btn btn-icon btn-sm btn-active-light-primary'
                                    title='Download'
                                    disabled={downloadingKeys[file.name]}
                                    onClick={() => handleDownloadClaimFile(file.name)}
                                  >
                                    {downloadingKeys[file.name] ? (
                                      <span className='spinner-border spinner-border-sm text-primary' />
                                    ) : (
                                      <Download size={16} className='text-primary' />
                                    )}
                                  </button>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )}

                      {cycle.medicalEnquiries && cycle.medicalEnquiries.length > 0 && (
                        <div className='mb-4'>
                          <div className='fw-bold text-gray-800 mb-3'>Linked Medical Enquiries</div>
                          <div className='d-flex flex-column gap-3'>
                            {cycle.medicalEnquiries.map((me) => (
                              <div key={me.id} className='border rounded p-4 bg-light-primary'>
                                {!me.repliedAt && (
                                  <div className='alert alert-warning py-2 px-3 mb-3 d-flex align-items-center gap-2'>
                                    <span className='fw-bold fs-7 text-warning'>
                                      {f({id: 'CLAIM.INPATIENT.SUPERSEDING_INLINE_REPLY_BANNER'}, {category: cycle.supersedingCategory || ''}) || `Enquiry on Superseding Request — ${cycle.supersedingCategory}`}
                                    </span>
                                  </div>
                                )}

                                <div className='mb-3'>
                                  <div className='fw-bold fs-7 mb-1'>Host Message:</div>
                                  <div className='bg-white p-3 rounded border text-gray-700'>
                                    {me.message}
                                  </div>
                                </div>

                                {me.documents && me.documents.length > 0 && (
                                  <div className='mb-3'>
                                    <div className='fw-bold fs-7 mb-2'>Documents:</div>
                                    <div className='d-flex flex-wrap gap-2'>
                                      {me.documents.map((doc, idx) => (
                                        <div key={idx} className='bg-white border rounded p-2 d-flex align-items-center gap-2'>
                                          {getFileIcon(getDocName(doc))}
                                          <span className='fs-7'>{getDocName(doc)}</span>
                                          <button className='btn btn-icon btn-sm btn-light' onClick={() => handleDownloadBase64(getDocName(doc))}>
                                            <Download size={14} />
                                          </button>
                                          <button className='btn btn-icon btn-sm btn-light' onClick={() => handleViewBase64(getDocName(doc))}>
                                            <Eye size={14} />
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {me.repliedAt ? (
                                  <div>
                                    <div className='fw-bold fs-7 mb-1 text-success'>Replied on {moment(me.repliedAt).format('DD MMM YYYY, HH:mm')}</div>
                                    <div className='bg-white p-3 rounded border text-gray-700'>
                                      {me.replyMessage}
                                    </div>
                                  </div>
                                ) : isImpersonation ? (
                                  <div className='text-muted fs-7 fst-italic mt-4'>
                                    Reply is disabled in impersonation mode.
                                  </div>
                                ) : (
                                  <div className='mt-4'>
                                    <div className='fw-bold fs-7 mb-2 text-primary'>Reply</div>
                                    <textarea
                                      className='form-control form-control-solid mb-2'
                                      rows={3}
                                      placeholder={f({id: 'CLAIM.INPATIENT.SUPERSEDING_REPLY_PLACEHOLDER'}) || 'Type your reply here...'}
                                      value={replyText[me.id] || ''}
                                      onChange={(e) => handleReplyChange(me.id, e.target.value)}
                                    />
                                    <div className='d-flex justify-content-end'>
                                      <button
                                        className='btn btn-sm btn-primary d-flex align-items-center gap-2'
                                        onClick={() => handleSubmitReply(me.id)}
                                        disabled={!replyText[me.id]?.trim() || answerMeMutation.isLoading}
                                      >
                                        {answerMeMutation.isLoading && answerMeMutation.variables?.idMe === me.id ? (
                                          <span className='spinner-border spinner-border-sm' />
                                        ) : (
                                          <Send size={14} />
                                        )}
                                        {f({id: 'CLAIM.INPATIENT.SUPERSEDING_REPLY_SUBMIT'}) || 'Submit Reply'}
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {(cycle.status === 'Approved' || cycle.status === 'Rejected') && (
                        <div className='mt-4 pt-4 border-top border-gray-200'>
                          <div className='text-uppercase text-gray-500 fw-bold fs-8 mb-3' style={{letterSpacing: '0.5px'}}>
                            {f({id: 'CLAIM.INPATIENT.SUPERSEDING_HOST_DECISION'}) || 'Host Decision'}
                          </div>
                          {cycle.preProcessRemark && (
                            <div className='d-flex mb-3'>
                              <div className='text-gray-500 flex-shrink-0' style={{width: '110px'}}>Remark</div>
                              <div className='fw-bold text-gray-800'>{cycle.preProcessRemark}</div>
                            </div>
                          )}
                          {cycle.decisionDocuments && cycle.decisionDocuments.length > 0 && (
                            <div className='d-flex flex-column gap-2'>
                              {cycle.decisionDocuments.map((doc, idx) => (
                                <div key={idx} className='d-flex align-items-center gap-2'>
                                  {getFileIcon(getDocName(doc))}
                                  {doc.url ? (
                                    <span
                                      className='text-primary fw-semibold cursor-pointer'
                                      style={{cursor: 'pointer'}}
                                      onClick={() => handleDownload(doc.url)}
                                    >
                                      {getDocName(doc)}
                                    </span>
                                  ) : (
                                    <span className='text-gray-800'>{getDocName(doc)}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </Modal>

      <Modal show={viewModalOpen} onHide={() => setViewModalOpen(false)} size='xl' centered>
        <Modal.Header closeButton>
          <Modal.Title>{viewingDocument?.fileName}</Modal.Title>
        </Modal.Header>
        <Modal.Body className='p-0' style={{height: '80vh'}}>
          {viewingDocument?.contentType === 'application/pdf' ? (
            <object
              data={`data:application/pdf;base64,${viewingDocument.base64Data}`}
              type='application/pdf'
              width='100%'
              height='100%'
            >
              <p>Unable to display PDF file.</p>
            </object>
          ) : viewingDocument?.contentType.startsWith('image/') ? (
            <div className='d-flex justify-content-center align-items-center h-100 bg-light'>
              <img
                src={`data:${viewingDocument.contentType};base64,${viewingDocument.base64Data}`}
                alt={viewingDocument.fileName}
                style={{maxWidth: '100%', maxHeight: '100%', objectFit: 'contain'}}
              />
            </div>
          ) : (
            <div className='d-flex justify-content-center align-items-center h-100'>
              <p>Preview not available for this file type.</p>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  )
}

export default SupersedingTrackingModal
